import React, { useRef } from 'react';
import { css, cx } from '@emotion/css';
import { toString, get } from 'lodash';
import colors from '@/theme/colors';
import { IconButton, Tooltip } from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { GradeChangeInput } from '@/pages/AccountInformation/AccountInformation.types';
import {
  BodyMediumMidEmphasis,
  TextInput,
  TitleLargeEmphasized,
  ButtonFlatMedium,
} from '@/design-system';
import { SelectInput } from '../AccountInformation/components/SelectInput/SelectInput';
import { Field, EmailState, FormSection } from './SignUp.container';
import { SCHOOL_FIELD_TYPE, GRADE_FIELD_TYPE, SCHOOL_NAME_TOOLTIP } from './SignUp.constants';
import SignUpFooter from './SignUpFooter';
import UserAlreadyExistsError from './UserAlreadyExistsError';
import { pxToRem } from '@/utils';
import { PersistentTooltip, Autocomplete } from '@/components';
import { useGoogleTranslator } from '@/hooks';

type Props = {
  email: EmailState;
  formSections: FormSection[];
  lookUpSchools: (options: any, callback: any) => void;
  onSchoolChange: (value: any, sectionId: string, fieldId: string) => void;
  onGradeChange: (input: GradeChangeInput, sectionId: string, fieldId: string) => void;
  onEmailChange: (value: string) => void;
  handleSubmit: (event: any) => void;
  onRemoveChild: (sectionId: string) => void;
  onAddChild: () => void;
};

type SchoolItem = {
  description: string;
  schoolId: number;
  schoolName: string;
  districtId: number;
  districtName: string;
  gradeLevels: string;
};

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const SignUpForm: React.FunctionComponent<Props> = ({
  email,
  formSections,
  onGradeChange,
  onEmailChange,
  lookUpSchools,
  onSchoolChange,
  onAddChild,
  onRemoveChild,
  handleSubmit,
}) => {
  const formSectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const addChildButtonRef = useRef<HTMLButtonElement | null>(null);
  const gradeLabel = useGoogleTranslator(`Grade (${currentYear}/${nextYear} school year)`);

  const setFocusOnFirstError = () => {
    if (email.error || email.value === '') {
      document.getElementById('emailInput')?.focus();
      return;
    }

    const emptyInputs = document.querySelectorAll('input[value=""]');

    for (const input of emptyInputs) {
      if (input.classList.contains('MuiSelect-nativeInput')) {
        const previousInput = input.previousElementSibling;
        if (previousInput) {
          (previousInput as HTMLInputElement).focus();
        }
        break;
      } else {
        (input as HTMLInputElement).focus();
        break;
      }
    }
  };

  const handleAddChild = () => {
    onAddChild();

    setTimeout(() => {
      const lastIndex = formSections.length;
      if (formSectionRefs.current[lastIndex]) {
        formSectionRefs.current[lastIndex]?.focus();
      }
    }, 0);
  };

  const handleRemoveChild = sectionId => {
    onRemoveChild(sectionId);

    setTimeout(() => {
      addChildButtonRef.current?.focus();
    }, 0);
  };

  const internalHandleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFocusOnFirstError();
    handleSubmit(event);
  };

  const getFieldComponent = ({ sectionId, field }: { sectionId: string; field: Field }) => {
    const {
      id,
      value,
      grades,
      renderGradesInput,
      error,
      errorText,
      type,
      hasToolTip,
      toolTipText,
    } = field;
    const fieldId = `${
      type === SCHOOL_FIELD_TYPE ? `${SCHOOL_FIELD_TYPE}-${id}` : `${GRADE_FIELD_TYPE}-${id}`
    }`;

    if (type === SCHOOL_FIELD_TYPE) {
      const fieldValue = get(value, 'schoolName', null);

      return (
        <div className={styles.fieldGroup}>
          <div
            className={cx(
              styles.inputWithToolTip,
              styles.inlined,
              styles.autocomplete,
              error && styles.inputSchoolError
            )}
          >
            <Autocomplete<SchoolItem>
              id={fieldId}
              lookup={lookUpSchools}
              label="School Name"
              value={fieldValue}
              onChange={val => {
                onSchoolChange(val, sectionId, id);
              }}
              isEqual={(s1, s2) => {
                return (s1 && s2 && s1.schoolId === s2.schoolId) || false;
              }}
              renderOption={val => (
                <>
                  {val.schoolName}
                  <span className={styles.secondaryName}>{val.districtName}</span>
                </>
              )}
              data-testid="schoolAutocompleteInput"
              error={error ? errorText : null}
              required
            />
          </div>
          {hasToolTip && (
            <span className={styles.tooltipButton}>
              <PersistentTooltip title={toolTipText} enterTouchDelay={0}>
                <IconButton aria-label={toolTipText}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </PersistentTooltip>
            </span>
          )}
        </div>
      );
    }

    if (!renderGradesInput || !grades) {
      return null;
    }

    const handleGradeChange = (input: GradeChangeInput, childUId: string) => {
      onGradeChange(input, sectionId, id);
    };
    return (
      <div className={cx(styles.inputWithToolTip, styles.gradeOptions)}>
        <SelectInput
          childUid={sectionId}
          picker={grade => [grade.description, toString(grade.id)]}
          // @ts-ignore
          options={grades}
          label={gradeLabel}
          // @ts-ignore
          value={value}
          onChange={handleGradeChange}
          error={error}
          errorMessage={errorText}
          required
        />
      </div>
    );
  };

  const onEmailInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    onEmailChange(event.target.value);
  };

  return (
    <main className={styles.container}>
      <TitleLargeEmphasized as="h1">
        Tell us a little bit about you and your family.
      </TitleLargeEmphasized>
      <p className={styles.formInstruction} aria-hidden="true">
        <span style={{ color: colors.dragon }}>*</span> Email, school, and grade are required.
      </p>
      <div className={styles.pageContainer}>
        <div className={styles.emailInput}>
          <TextInput
            id="emailInput"
            name="emailInput"
            label="Email Address"
            placeholder="example@email.com"
            type="email"
            value={email.value}
            error={!!email.error}
            helperText={email.error ? email.errorText : ''}
            onChange={onEmailInputChange}
            autoComplete="email"
            required
          />
          {email.userAlreadyExists && <UserAlreadyExistsError />}
        </div>
        <div className={styles.formContainer}>
          <form onSubmit={internalHandleSubmit}>
            {formSections.map(({ label, id: sectionId, renderId, fields }, index) => {
              const isRemovableSection = renderId > 1;

              return (
                <React.Fragment key={sectionId}>
                  <div className={styles.cardBorder} />
                  <div
                    className={styles.formSection}
                    ref={el => (formSectionRefs.current[index] = el)}
                    tabIndex={-1}
                    role="group"
                    aria-labelledby={`signup-section-label-${sectionId}`}
                  >
                    <div className={styles.formSectionHeadline}>
                      <BodyMediumMidEmphasis id={`signup-section-label-${sectionId}`}>
                        {label}
                      </BodyMediumMidEmphasis>
                    </div>
                    {fields.map(field => getFieldComponent({ sectionId, field }))}
                    {isRemovableSection && (
                      <div
                        className={cx(styles.removeIcon, styles.tooltipButton)}
                        onClick={() => handleRemoveChild(sectionId)}
                      >
                        <Tooltip title="Remove Child" placement="right" enterTouchDelay={0}>
                          <IconButton aria-label="Remove Child">
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}
            <div>
              <ButtonFlatMedium
                onClick={handleAddChild}
                data-testid="addChildButton"
                startAdornment={<AddIcon />}
                ref={addChildButtonRef}
              >
                Add Child
              </ButtonFlatMedium>
            </div>
            <SignUpFooter handleSubmit={internalHandleSubmit} />
          </form>
        </div>
      </div>
    </main>
  );
};

const styles = {
  container: css`
    padding: 0px 16px;
  `,
  pageContainer: css`
    margin-top: 24px;
  `,
  formInstruction: css`
    font-family: 'Montserrat Regular', Arial, Helvetica, sans-serif;
    font-size: 14px;
    color: ${colors.slate};
    margin-top: 16px;
  `,
  emailInput: css``,
  formContainer: css`
    margin: 24px 0;
  `,
  formSection: css`
    background-color: ${colors.platinum};
    padding: 24px 16px 40px 24px;
    border-radius: 6px;
    margin-bottom: 16px;
    position: relative;
  `,
  formSectionHeadline: css`
    padding-bottom: 16px;
  `,
  fieldGroup: css`
    position: relative;
    margin-top: 21px;
    display: flex;
    align-items: center;
  `,
  inputWithToolTip: css`
    width: 90% !important;
  `,
  gradeOptions: css`
    padding: 0;
    margin-top: 24px;
    & .MuiInputLabel-root {
      font-family: 'Montserrat Regular';
      line-height: 1.45;
      letter-spacing: -0.4px;
      color: ${colors.charcoal} !important;
      width: 100%;
      white-space: normal;
      overflow: visible;
      text-overflow: unset;
    }
    .MuiInputBase-input {
      font-family: 'Montserrat Regular' !important;
      font-size: ${pxToRem(16)};
      line-height: 1.43;
      letter-spacing: -0.4px;
      color: ${colors.prussian};
    }
    .MuiInputBase-input {
      border-bottom-color: ${colors.prussian} !important;
    }
  `,
  inlined: css`
    display: inline-block;
  `,
  tooltip: css`
    position: absolute;
    top: 3px;
  `,
  inputSchoolError: css`
    .MuiInput-underline:before {
      border-bottom: 1px solid ${colors.dragon};
    }
  `,
  linkMe: css`
    color: ${colors.jungle} !important;
    text-decoration: underline;
    cursor: pointer;
  `,
  removeIcon: css`
    position: absolute;
    top: 4px;
    right: 4px;
    cursor: pointer;
  `,
  secondaryName: css`
    font-size: ${pxToRem(13)};
    font-family: 'Proxima Nova', Helvetica, Arial, sans-serif;
    color: ${colors.slate};
    display: block;
  `,
  tooltipButton: css`
    & .MuiIconButton-root {
      color: ${colors.prussian};
    }
    & .MuiIconButton-root:hover {
      background-color: rgba(97, 193, 108, 0.15);
    }
  `,
  schoolTooltip: css`
    padding-top: 12px;
  `,
  messageError: css`
    font-family: 'Montserrat Regular';
    font-size: ${pxToRem(12)};
    color: ${colors.dragon};
  `,
  autocomplete: css`
    .MuiFormControl-root {
      height: auto !important;
    }
    .MuiFormLabel-root {
      font-family: 'Montserrat Regular';
      line-height: 1.45;
      letter-spacing: -0.4px;
      color: ${colors.charcoal} !important;
    }
    .MuiInputBase-root {
      font-family: 'Montserrat Regular';
      font-size: ${pxToRem(16)};
      line-height: 1.5;
      letter-spacing: -0.5px;
      color: ${colors.prussian};
    }
    .MuiInputBase-input {
      font-family: 'Montserrat Regular' !important;
      font-size: ${pxToRem(16)} !important;
      line-height: 1.43;
      letter-spacing: -0.4px;
      color: ${colors.prussian};
    }
  `,
  cardBorder: css`
    height: 1px;
    border-bottom: 1px solid ${colors.silver};
    z-index: 1;
    position: relative;
    top: 64px;
    margin: 0px 1px;
  `,
};

export default SignUpForm;
